/**
 * BiOui v2 (17/10/2026), piste A « Bande bleue » : comportements de l'en-tête et composants communs.
 * Module ES différé, sans dépendance (le compteur du panier écoute l'événement standard de Shopify).
 *  1. Survol « liquide » du bouton principal (position du pointeur → --x / --y), coupé en mouvement réduit.
 *  2. Champ-autocollant « Département ou produit » : combobox ARIA (flèches, Entrée, Échap), 1re ligne = page
 *     département, puis jusqu'à 3 produits (API Section Rendering, section ai-suggestions), ligne « Plaques
 *     d'immatriculation » si la requête contient « plaque », « Rechercher “…” dans tout le site » en dernier.
 *  3. Panneau « Départements » : clic, survol après 350 ms, fermeture à la sortie (300 ms), Échap, flèches.
 *  4. Tiroir mobile : construit à la 1re ouverture à partir du panneau (balisage rendu une seule fois).
 *  5. En-tête replié au défilement (sans décalage de mise en page) ; variante A2 : champ d'en-tête masqué tant que
 *     le champ du héros est visible.
 *  6. Compteur « Panier (n) » tenu à jour après chaque ajout.
 *  7. Petits composants : logos médias (pause, WCAG 2.2.2), rangée défilante à boutons, colonne régions.
 */
const $ = (s, c = document) => c.querySelector(s);
const $$ = (s, c = document) => [...c.querySelectorAll(s)];
const reduit = matchMedia('(prefers-reduced-motion: reduce)');
const esc = (t) => String(t ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
const entete = $('[data-ai-entete]');

/* 1. Survol « liquide » ------------------------------------------------------------------------------------ */
document.addEventListener(
  'pointermove',
  (ev) => {
    if (reduit.matches || ev.pointerType !== 'mouse') return;
    const b = ev.target instanceof Element && ev.target.closest('.ai-btn--principal, .button:not(.button-secondary, .button-unstyled)');
    if (!(b instanceof HTMLElement)) return;
    const r = b.getBoundingClientRect();
    b.style.setProperty('--x', `${ev.clientX - r.left}px`);
    b.style.setProperty('--y', `${ev.clientY - r.top}px`);
  },
  { passive: true }
);

/* 2. Champ-autocollant ------------------------------------------------------------------------------------- */
const norme = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, ' ').trim();

/** Distance d'édition bornée (tolère les fautes : « morbian » → Morbihan). Reprise du lot 1. */
function distance(a, b, max = 3) {
  if (Math.abs(a.length - b.length) > max) return max + 1;
  let prec = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    const cour = [i];
    let mini = i;
    for (let j = 1; j <= b.length; j++) {
      cour[j] = Math.min(prec[j] + 1, cour[j - 1] + 1, prec[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
      mini = Math.min(mini, cour[j]);
    }
    if (mini > max) return max + 1;
    prec = cour;
  }
  return prec[b.length];
}

/** Score d'un département pour une requête normalisée (0 = écarté). Reprise du lot 1. */
function score(d, q) {
  const num = d.n.toLowerCase();
  if (/^\d[\dab]?\d?$/.test(q)) {
    const qn = q.length === 1 ? '0' + q : q;
    if (num === q || num === qn) return 1000;
    if (num.startsWith(q)) return 500 - num.length;
    return 0;
  }
  let meilleur = 0;
  for (const nom of [d.nom, ...(d.alias || [])]) {
    const t = norme(nom);
    const estAlias = nom !== d.nom ? 5 : 0;
    if (t === q) meilleur = Math.max(meilleur, 300 - estAlias);
    else if (t.startsWith(q)) meilleur = Math.max(meilleur, 250 - estAlias);
    else if (t.split(' ').some((m) => m.startsWith(q))) meilleur = Math.max(meilleur, 200 - estAlias);
    else if (q.length >= 3 && t.includes(q)) meilleur = Math.max(meilleur, 150 - estAlias);
    else if (q.length >= 4) {
      const mots = [t, ...t.split(' ').filter((m) => m.length >= 4)];
      const dist = Math.min(...mots.map((m) => distance(q, m.slice(0, Math.max(q.length, m.length)))));
      const tolere = q.length >= 7 ? 2 : 1;
      if (dist <= tolere) meilleur = Math.max(meilleur, 100 - dist * 10 - estAlias);
    }
  }
  return meilleur;
}

let table = null;
let promesseTable = null;
function chargerTable() {
  if (table) return Promise.resolve(table);
  if (!promesseTable) {
    promesseTable = fetch(entete?.dataset.table || '')
      .then((r) => r.json())
      .then((t) => (table = t))
      .catch(() => (table = []));
  }
  return promesseTable;
}
/** Départements correspondant à la requête : [liste, unique ?] */
function departements(q) {
  const qs = /^\d/.test(q) ? q.replace(/\s/g, '') : q;
  const notes = table
    .map((d) => ({ d, s: score(d, qs) }))
    .filter((x) => x.s > 0)
    .sort((a, b) => b.s - a.s);
  if (!notes.length) return [[], false];
  const [a, b] = notes;
  // Un seul département retenu : une seule réponse, un numéro exact (« 56 », « 2a », « 974 »), ou un nom exact
  // sans autre nom qui commence pareil (« morbihan », mais pas « loire » ni « corse »).
  const unique = notes.length === 1 || (a.s === 1000 && qs.length >= 2) || (a.s >= 295 && a.s < 1000 && (!b || b.s < 245));
  return [unique ? [a.d] : notes.slice(0, 4).map((x) => x.d), unique];
}
const cacheProduits = new Map();
function produits(url) {
  if (!cacheProduits.has(url)) {
    cacheProduits.set(
      url,
      fetch(url, { headers: { Accept: 'text/html' } })
        .then((r) => (r.ok ? r.text() : ''))
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          return $$('[data-ai-sugg-produits] li', doc).map((li) => ({ ...li.dataset }));
        })
        .catch(() => [])
    );
  }
  return cacheProduits.get(url);
}

function initChamp(form) {
  const input = $('.ai-champ__saisie', form);
  const liste = $('.ai-champ__liste', form);
  const statut = $('[data-ai-statut]', form);
  if (!input || !liste) return;
  let actif = -1;
  let jeton = 0;
  let resultats = [];
  let enCours = Promise.resolve();
  const max = () => (matchMedia('(max-width: 989px)').matches ? 6 : 8);
  const options = () => $$('.ai-sugg', liste);

  function fermer() {
    liste.hidden = true;
    input.setAttribute('aria-expanded', 'false');
    input.removeAttribute('aria-activedescendant');
    actif = -1;
  }
  function marquer(i) {
    const o = options();
    o.forEach((x, k) => x.setAttribute('aria-selected', String(k === i)));
    actif = i;
    if (o[i]) {
      input.setAttribute('aria-activedescendant', o[i].id);
      o[i].scrollIntoView({ block: 'nearest' });
    }
  }
  function ligne(r, i) {
    const li = document.createElement('li');
    li.className = 'ai-sugg';
    li.id = `${input.id}-opt-${i}`;
    li.setAttribute('role', 'option');
    li.setAttribute('aria-selected', 'false');
    li.dataset.u = r.u;
    if (r.type === 'dep') {
      li.classList.add('ai-sugg--dep');
      li.innerHTML = `<span class="ai-sugg__n">${esc(r.d.n)}</span><span><span class="ai-sugg__titre">${esc(r.d.nom.replace(/'/g, '’'))}</span><br><span class="ai-sugg__meta">${esc(r.d.r || 'Département')}, ${r.moto ? 'format moto' : 'tous les modèles'}</span></span>`;
    } else if (r.type === 'prod') {
      const badge = r.f ? `<span class="ai-badge${r.f === 'Moto' ? ' ai-badge--moto' : ''}">${esc(r.f)}</span>` : '';
      li.innerHTML = `<span><span class="ai-sugg__titre">${esc(r.t)}</span>${r.m ? `<br><span class="ai-sugg__meta">${esc(r.m)}</span>` : ''}</span><span class="ai-sugg__droite">${badge}<span class="ai-sugg__prix">${esc(r.p)}</span></span>`;
    } else if (r.type === 'plaque') {
      const prix = entete?.dataset.plaquePrix;
      li.innerHTML = `<span><span class="ai-sugg__titre">Plaques d’immatriculation</span><br><span class="ai-sugg__meta">Plexiglas ou aluminium, sur mesure</span></span>${prix ? `<span class="ai-sugg__droite"><span class="ai-sugg__prix">dès ${esc(prix)}</span></span>` : ''}`;
    } else {
      li.classList.add('ai-sugg--tout');
      li.innerHTML = `<span>Rechercher <strong>« ${esc(r.q)} »</strong> dans tout le site</span>`;
    }
    return li;
  }
  function afficher() {
    const n = Math.min(resultats.length, max());
    liste.replaceChildren(...resultats.slice(0, n).map(ligne));
    liste.hidden = n === 0;
    input.setAttribute('aria-expanded', String(n > 0));
    actif = -1;
    input.removeAttribute('aria-activedescendant');
    if (statut) statut.textContent = n > 1 ? `${n - 1} suggestion${n > 2 ? 's' : ''}. Flèches pour choisir.` : '';
  }
  async function calculer() {
    const brut = input.value.trim();
    const moi = ++jeton;
    if (!brut) return fermer();
    const moto = /\bmoto\b/i.test(brut);
    const q = norme(brut.replace(/\bmoto\b/gi, ' ')) || norme(brut);
    await chargerTable();
    if (moi !== jeton) return;
    const [deps, unique] = departements(q);
    const base = deps.map((d) => ({ type: 'dep', d, moto, u: d.url + (moto ? '#ai-format-moto' : '') }));
    const fin = [];
    if (norme(brut).includes('plaque')) fin.push({ type: 'plaque', u: entete?.dataset.plaqueUrl || '/collections/plaque-immatriculation' });
    fin.push({ type: 'tout', q: brut, u: `${entete?.dataset.recherche || '/search'}?q=${encodeURIComponent(brut)}` });
    resultats = [...base, ...fin];
    afficher();
    // Produits : ceux du département (s'il est unique) ou ceux de la recherche prédictive (aucun département)
    let url = '';
    if (unique) url = `${deps[0].url.split('/').slice(0, 3).join('/')}?section_id=ai-suggestions`;
    else if (!deps.length && q.length >= 2)
      url = `${entete?.dataset.suggest || '/search/suggest'}?q=${encodeURIComponent(brut)}&resources%5Btype%5D=product&resources%5Blimit%5D=4&section_id=ai-suggestions`;
    if (!url) return;
    let prods = await produits(url);
    if (moi !== jeton || !prods.length) return;
    if (moto) prods = [...prods.filter((p) => p.f === 'Moto'), ...prods.filter((p) => p.f !== 'Moto')];
    const avant = actif;
    resultats = [...base, ...prods.slice(0, 3).map((p) => ({ type: 'prod', u: p.u, t: p.t, m: p.m, f: p.f, p: p.p })), ...fin];
    afficher();
    if (avant >= 0) marquer(Math.min(avant, options().length - 1));
  }
  const rendre = () => (enCours = calculer());
  function suivre(li) {
    if (!li?.dataset.u) return;
    fermer();
    location.href = li.dataset.u;
  }
  /** Entrée sans choix : la 1re suggestion (page département), dès que la table est chargée. */
  async function premiere() {
    await chargerTable();
    const [deps] = departements(norme(input.value.replace(/\bmoto\b/gi, ' ')) || norme(input.value));
    if (deps.length) {
      const moto = /\bmoto\b/i.test(input.value);
      location.href = deps[0].url + (moto ? '#ai-format-moto' : '');
    } else form.submit();
  }
  input.addEventListener('focus', () => {
    chargerTable();
    if (input.value.trim()) rendre();
  });
  input.addEventListener('input', rendre);
  input.addEventListener('keydown', (ev) => {
    const o = options();
    if (ev.key === 'ArrowDown') {
      ev.preventDefault();
      if (liste.hidden) rendre();
      else marquer(Math.min(actif + 1, o.length - 1));
    } else if (ev.key === 'ArrowUp') {
      ev.preventDefault();
      marquer(Math.max(actif - 1, 0));
    } else if (ev.key === 'Escape') {
      if (!liste.hidden) {
        ev.stopPropagation();
        fermer();
      }
    } else if (ev.key === 'Enter') {
      if (!input.value.trim()) return;
      ev.preventDefault();
      if (o[actif]) suivre(o[actif]);
      else premiere();
    }
  });
  form.addEventListener('submit', (ev) => {
    if (!input.value.trim()) {
      ev.preventDefault();
      input.focus();
      return;
    }
    if (ev.submitter) {
      // Bouton loupe : même règle qu'Entrée
      ev.preventDefault();
      const o = options();
      if (o[actif]) suivre(o[actif]);
      else premiere();
    }
  });
  liste.addEventListener('mousedown', (ev) => ev.preventDefault());
  liste.addEventListener('click', (ev) => suivre(ev.target instanceof Element && ev.target.closest('.ai-sugg')));
  document.addEventListener('click', (ev) => {
    if (!form.contains(/** @type {Node} */ (ev.target))) fermer();
  });
  input.addEventListener('blur', () => setTimeout(() => !form.contains(document.activeElement) && fermer(), 150));
  form.aiAttendre = () => enCours;
}
$$('[data-ai-champ]').forEach(initChamp);

/* 3. Panneau « Départements » ------------------------------------------------------------------------------- */
const bouton = $('[data-ai-panneau-bouton]');
const pan = $('[data-ai-panneau]');
if (bouton && pan) {
  let tOuvre;
  let tFerme;
  // Ouvert par un clic (ou au clavier) : le panneau est « épinglé », la sortie de la souris ne le ferme plus ; il se
  // ferme au 2e clic sur le bouton, au clic ailleurs, par Échap ou par la croix. Ouvert au simple survol : il se ferme
  // à la sortie. Un clic sur un panneau ouvert par survol l'épingle au lieu de le refermer (survol puis clic).
  let epingle = false;
  const ouvert = () => !pan.hidden;
  const survol = matchMedia('(hover: hover) and (pointer: fine)');
  const ouvrir = (focus, parClic) => {
    clearTimeout(tFerme);
    if (parClic) epingle = true;
    pan.hidden = false;
    bouton.setAttribute('aria-expanded', 'true');
    if (focus) $('a', pan)?.focus();
  };
  const fermerPan = (retour) => {
    clearTimeout(tOuvre);
    epingle = false;
    pan.hidden = true;
    bouton.setAttribute('aria-expanded', 'false');
    if (retour) bouton.focus();
  };
  bouton.addEventListener('click', () => {
    clearTimeout(tOuvre);
    if (ouvert() && epingle) fermerPan();
    else ouvrir(false, true);
  });
  bouton.addEventListener('keydown', (ev) => {
    if (ev.key === 'ArrowDown') {
      ev.preventDefault();
      ouvrir(true, true);
    }
  });
  [bouton, pan].forEach((el) => {
    el.addEventListener('mouseenter', () => {
      if (!survol.matches) return;
      clearTimeout(tFerme);
      if (!ouvert()) tOuvre = setTimeout(() => ouvrir(false), 350);
    });
    el.addEventListener('mouseleave', () => {
      if (!survol.matches || epingle) return;
      clearTimeout(tOuvre);
      tFerme = setTimeout(() => fermerPan(false), 300);
    });
  });
  $('[data-ai-panneau-fermer]', pan)?.addEventListener('click', () => fermerPan(true));
  document.addEventListener('keydown', (ev) => {
    if (ev.key === 'Escape' && ouvert()) fermerPan(pan.contains(document.activeElement) || document.activeElement === bouton);
  });
  document.addEventListener('click', (ev) => {
    const t = /** @type {Node} */ (ev.target);
    if (ouvert() && !pan.contains(t) && !bouton.contains(t)) fermerPan(false);
  });
  pan.addEventListener('keydown', (ev) => {
    if (!['ArrowDown', 'ArrowUp'].includes(ev.key)) return;
    const liens = $$('a', pan);
    const i = liens.indexOf(/** @type {HTMLAnchorElement} */ (document.activeElement));
    if (i < 0) return;
    ev.preventDefault();
    liens[Math.max(0, Math.min(liens.length - 1, i + (ev.key === 'ArrowDown' ? 1 : -1)))].focus();
  });
}

/* 4. Tiroir mobile ----------------------------------------------------------------------------------------- */
const tiroir = $('[data-ai-tiroir]');
let declencheur = null;
function construireTiroir() {
  const nav = $('[data-ai-tiroir-nav]');
  if (!nav || nav.dataset.fait) return;
  nav.dataset.fait = '1';
  const ici = location.pathname;
  const lien = (a, cls = '', texte = null) => {
    const href = a.getAttribute('href');
    const cour = href === ici ? ' aria-current="page"' : '';
    return `<a${cls ? ` class="${cls}"` : ''} href="${esc(href)}"${cour}>${texte ?? a.innerHTML}</a>`;
  };
  const chev = '<svg aria-hidden="true" focusable="false"><use href="#ai-i-chevron"/></svg>';
  let ouvrirDeps = false;
  let h = '';
  // Ordre du tiroir : régions des colonnes, puis l'outre-mer (1re colonne du panneau) en dernier
  const groupes = $$('.ai-panneau__groupe');
  groupes.sort((a, b) => (a.closest('.ai-panneau__col:first-child') ? 1 : 0) - (b.closest('.ai-panneau__col:first-child') ? 1 : 0));
  for (const g of groupes) {
    const titre = $('.ai-panneau__titre', g);
    const deps = $$('.ai-panneau__deps a', g);
    const ouvert = deps.some((a) => a.getAttribute('href') === ici) || titre?.getAttribute('href') === ici;
    ouvrirDeps ||= ouvert;
    h += `<details class="ai-acc ai-acc--region"${ouvert ? ' open' : ''}><summary>${esc(titre?.textContent.trim())}${chev}</summary><ul class="ai-acc__deps">`;
    if (titre) h += `<li>${lien(titre, 'ai-acc__toute', `Toute la région ${esc(titre.textContent.trim())}`)}</li>`;
    h += deps.map((a) => `<li>${lien(a)}</li>`).join('');
    h += '</ul></details>';
  }
  const raccourcis = $$('[data-ai-raccourcis] a').map((a) => `<li>${lien(a, 'ai-acc__toute', esc(a.textContent.trim()))}</li>`).join('');
  nav.innerHTML =
    `<details class="ai-acc"${ouvrirDeps ? ' open' : ''} data-ai-acc-departements><summary>Départements${chev}</summary><div class="ai-acc__corps"><ul class="ai-acc__deps">${raccourcis}</ul>${h}</div></details>` +
    $$('.ai-entete__nav .ai-nav a.ai-nav__lien').map((a) => lien(a, 'ai-acc__lien', esc(a.textContent.trim()))).join('') +
    `<div class="ai-tiroir__secondaire">${$$('.ai-entete__actions a.ai-action--compact').map((a) => lien(a, 'ai-btn ai-btn--secondaire', esc(a.textContent.trim()))).join('')}</div>`;
}
function ouvrirTiroir(src, focusChamp) {
  construireTiroir();
  declencheur = src || null;
  tiroir.hidden = false;
  document.documentElement.style.overflow = 'hidden';
  $$('[data-ai-tiroir-ouvrir]').forEach((b) => b.setAttribute('aria-expanded', 'true'));
  (focusChamp ? $('#ai-champ-tiroir') : $('.ai-tiroir__fermer', tiroir))?.focus();
}
function fermerTiroir() {
  tiroir.hidden = true;
  document.documentElement.style.overflow = '';
  $$('[data-ai-tiroir-ouvrir]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
  declencheur?.focus();
}
if (tiroir) {
  $$('[data-ai-tiroir-ouvrir]').forEach((b) => b.addEventListener('click', () => ouvrirTiroir(b, b.hasAttribute('data-ai-focus-champ'))));
  $$('[data-ai-tiroir-fermer]', tiroir).forEach((b) => b.addEventListener('click', fermerTiroir));
  tiroir.addEventListener('keydown', (ev) => {
    if (ev.key === 'Escape') return fermerTiroir();
    if (ev.key !== 'Tab') return;
    const f = $$('a[href], button, input:not([type=hidden]), summary', tiroir).filter((x) => x.offsetParent !== null);
    if (!f.length) return;
    if (ev.shiftKey && document.activeElement === f[0]) {
      ev.preventDefault();
      f[f.length - 1].focus();
    } else if (!ev.shiftKey && document.activeElement === f[f.length - 1]) {
      ev.preventDefault();
      f[0].focus();
    }
  });
  matchMedia('(min-width: 990px)').addEventListener('change', (m) => m.matches && !tiroir.hidden && fermerTiroir());
}

/* 5. En-tête replié au défilement ; variante A2 -------------------------------------------------------------- */
if (entete) {
  const wrapper = $('.page-wrapper');
  const position = () => Math.max(window.scrollY, wrapper ? wrapper.scrollTop : 0);
  let attente = false;
  const maj = () => {
    attente = false;
    entete.classList.toggle('est-replie', position() > 40);
  };
  const auDefilement = () => {
    if (!attente) {
      attente = true;
      requestAnimationFrame(maj);
    }
  };
  addEventListener('scroll', auDefilement, { passive: true });
  wrapper?.addEventListener('scroll', auDefilement, { passive: true });
  maj();
  const heros = $('[data-ai-champ-heros]');
  if (heros) {
    entete.classList.add('ai-entete--a2');
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(([x]) => entete.classList.toggle('montre-champ', !x.isIntersecting), { rootMargin: '-140px 0px 0px 0px' }).observe(heros);
    } else entete.classList.add('montre-champ');
  }
}

/* 6. Compteur du panier ------------------------------------------------------------------------------------ */
function majPanier(n) {
  $$('[data-ai-nb]').forEach((el) => (el.textContent = `(${n})`));
  $$('[data-ai-panier]').forEach((el) => el.setAttribute('aria-label', `Panier, ${n} article${n > 1 ? 's' : ''}`));
}
import('@shopify/events')
  .then(({ StandardEvents }) => {
    document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
      event.promise?.then(({ cart, detail }) => majPanier(cart?.totalQuantity ?? detail?.itemCount ?? 0)).catch(() => {});
    });
  })
  .catch(() => {});
addEventListener('pageshow', (ev) => {
  if (ev.persisted)
    fetch('/cart.js')
      .then((r) => r.json())
      .then((c) => majPanier(c.item_count))
      .catch(() => {});
});

/* 7. Petits composants ------------------------------------------------------------------------------------- */
// Logos médias : bouton pause (WCAG 2.2.2)
$$('[data-ai-pause]').forEach((b) =>
  b.addEventListener('click', () => {
    const zone = b.closest('.ai-medias');
    const pause = zone.classList.toggle('est-en-pause');
    b.setAttribute('aria-pressed', String(pause));
    const t = $('span', b);
    if (t) t.textContent = pause ? 'Reprendre' : 'Mettre en pause';
    $('use', b)?.setAttribute('href', pause ? '#ai-i-lecture' : '#ai-i-pause');
  })
);
// Rangée défilante à boutons (régions) : défilement manuel seulement
$$('[data-ai-defile]').forEach((b) =>
  b.addEventListener('click', () => {
    const l = document.getElementById(b.getAttribute('aria-controls'));
    l?.scrollBy({ left: Number(b.dataset.aiDefile) * l.clientWidth * 0.8, behavior: reduit.matches ? 'auto' : 'smooth' });
  })
);
$$('[data-ai-defilant]').forEach((l) => {
  const btns = $$(`[data-ai-defile][aria-controls="${l.id}"]`);
  const etat = () => {
    const [g, d] = btns;
    if (g) g.disabled = l.scrollLeft < 4;
    if (d) d.disabled = l.scrollLeft + l.clientWidth > l.scrollWidth - 4;
  };
  l.addEventListener('scroll', etat, { passive: true });
  etat();
});
// Colonne régions → départements (collections)
$$('.ai-navcol__bascule').forEach((b) =>
  b.addEventListener('click', () => {
    const l = document.getElementById(b.getAttribute('aria-controls'));
    const ouvrir = b.getAttribute('aria-expanded') !== 'true';
    b.setAttribute('aria-expanded', String(ouvrir));
    if (l) l.hidden = !ouvrir;
  })
);
